import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'
import {InfoCircleIcon} from '@/components/ui/icon/Icon'
import {CreateMedicalRecordButton} from '@/features/medical-records/components/create-medical-record-button/CreateMedicalRecordButton'
import {
    StyledMedicalRecordEmptyState,
    StyledMedicalRecordEmptyStateButtons,
    StyledMedicalRecordEmptyStateCopyWrapper
} from '@/features/medical-records/components/medical-record-empty-state/style'
import {usePermissions} from '@/features/auth/store/store'
import {PatientExtended} from '@/features/patients/types'
import {Text} from '@/components/ui/text/Text'

export const MedicalRecordEmptyState = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const canCreateReport = useMemo(() => {
        const hasResidenceAndProject = !!patient.residenceAslDistrict && !!patient.project
        const hasResidenceAndDiagnosis = !!patient.residenceAslDistrict && !!patient.neuromuscularDiagnosis
        const hasOnlyDiagnosis = !patient.residenceAslDistrict && !!patient.neuromuscularDiagnosis

        return (
            permissions?.medicalRecords.write &&
            (hasResidenceAndProject || hasResidenceAndDiagnosis || hasOnlyDiagnosis)
        )
    }, [patient.residenceAslDistrict, patient.project, patient.neuromuscularDiagnosis])

    // Prepare search params to pass to create report page if needed
    const searchParams = useMemo(() => {
        return {
            projectId: patient.project?.id
        }
    }, [patient.project?.id])

    return (
        <StyledMedicalRecordEmptyState direction="column" gap={2} align="center" fullWidth>
            <InfoCircleIcon size={15} />
            <StyledMedicalRecordEmptyStateCopyWrapper>
                <Text size="textSm">{t('medical_records:not_existent_medical_record')}</Text>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('medical_records:not_existent_medical_record_description')}
                </Text>
                <StyledMedicalRecordEmptyStateButtons gap={2} justify="center" align="center">
                    <CreateMedicalRecordButton patient={patient} variant="secondary" />
                    {canCreateReport && (
                        <CreateMedicalRecordButton
                            patient={patient}
                            variant="primaryNeutral"
                            navigateToCreateReport={true}
                            searchParams={searchParams}
                        >
                            {t('commons:report', {count: 1})}
                        </CreateMedicalRecordButton>
                    )}
                </StyledMedicalRecordEmptyStateButtons>
            </StyledMedicalRecordEmptyStateCopyWrapper>
        </StyledMedicalRecordEmptyState>
    )
}
